import { Planner, PlannerWithDetails } from '@/common/types/databaseTypes';
import { isAfterDate } from '@tickitacka/shared';
import { UpdateContext } from '@/features/notification/constants/notification';
import { useTodoTimeChangedNotification } from '@/features/notification/services/factoryNotificationServices';
import { isDifferent } from '@/features/notification/utils/notificationUtils';
import {
    QueryClient,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import { isSameDay } from 'date-fns';
import {
    CreatePlannerInput,
    CreateTodoWithPlannerInput,
    plannerService,
} from './plannerServices';

import { todoService } from '@/features/todo/services/todoService';
import { workspaceService } from '@/features/workspace/services/workspaceService';
import { useAuthStore } from '@/stores/authStore';

export const invalidatePlannerQueries = async (
    queryClient: QueryClient,
    planner: Partial<Planner>
) => {
    // 플래너 관련 쿼리 무효화
    await queryClient.invalidateQueries({ queryKey: ['planners'] });
    await queryClient.invalidateQueries({ queryKey: ['todos', 'inbox'] });
};

// 단일 플래너 조회 훅
export function usePlanner(plannerId: string) {
    return useQuery<Planner, Error>({
        queryKey: ['planner', plannerId],
        queryFn: () => plannerService.getPlannerById(plannerId),
        enabled: !!plannerId, // plannerId가 있을 때만 쿼리 실행
    });
}

// 특정 투두의 플래너 목록 조회 훅
export function usePlannersByTodo(todoId: string) {
    return useQuery<Planner[], Error>({
        queryKey: ['planners', 'todo', todoId],
        queryFn: () => plannerService.getPlannersByTodoId(todoId),
        enabled: !!todoId,
    });
}

export function usePlannersByDate(date: Date, userId: string) {
    return useQuery<PlannerWithDetails[], Error>({
        queryKey: ['planners', 'selectedDate', userId, date.toDateString()],
        queryFn: () => plannerService.getPlannersByDate(date, userId),
        enabled: !!date && !!userId,
    });
}

export function useUnfinishedPlanners(userId: string, targetDate: Date) {
    return useQuery<PlannerWithDetails[], Error>({
        queryKey: ['planners', 'unfinished', userId, targetDate.toDateString()],
        queryFn: () => plannerService.getUnfinishedPlanners(userId, targetDate),
        enabled: !!userId && !!targetDate,
    });
}

// 플래너 화면에 필요한 모든 할 일 데이터를 한 번에 조회하는 통합 훅
export function usePlannersWithTodo(userId: string, selectedDate: Date) {
    // 오늘 날짜 확인
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const isToday = isSameDay(today, selectedDate);
    const isAfterToday = isAfterDate(selectedDate, today);

    const showUnfinished = isToday || isAfterToday;

    // 오늘 날짜 이후인 경우에만 미완료 할 일을 로드, 아닌 경우는 enabled: false로 설정
    const unfinishedPlanners = useUnfinishedPlanners(
        userId,
        isToday ? today : tomorrow
    ); // goal.status_code 체크 O
    const todayPlanners = usePlannersByDate(selectedDate, userId); // goal.status_code 체크 X

    return {
        // 할 일 데이터 - showUnfinished가 true면 unfinishedPlanners, false면 빈 배열
        unfinishedPlanners: showUnfinished ? unfinishedPlanners.data : [],
        todayPlanners: todayPlanners.data,

        // 로딩 상태 - showUnfinished가 true면 unfinishedPlanners의 로딩 상태, false면 todayPlanners의 로딩 상태
        isLoading: showUnfinished
            ? unfinishedPlanners.isLoading || todayPlanners.isLoading
            : todayPlanners.isLoading,

        // 에러 상태 - showUnfinished가 true면 unfinishedPlanners의 에러, false면 todayPlanners의 에러
        error: showUnfinished
            ? unfinishedPlanners.error || todayPlanners.error
            : todayPlanners.error,

        // 리프레시 함수
        refresh: async () => {
            try {
                if (showUnfinished) {
                    await Promise.all([
                        unfinishedPlanners.refetch(),
                        todayPlanners.refetch(),
                    ]);
                } else {
                    await todayPlanners.refetch();
                }
            } catch (error) {
                console.error('Failed to refresh planners:', error);
            }
        },
    };
}

// 멤버의 upcoming 목록 조회 훅
export function useMemberUpcomingPlannerCalendar(
    userId: string,
    selectedDate: Date
) {
    return useQuery({
        queryKey: ['planners', 'upcoming', userId, selectedDate.toDateString()],
        queryFn: () => plannerService.getUpcomingPlanners(userId, selectedDate),
        enabled: !!userId && !!selectedDate,
    });
}

// 멤버의 upcoming 목록 조회 훅
export function useGetPaginatedUpcomingPlanners(userId: string) {
    const PAGE_SIZE = 10;

    return useInfiniteQuery({
        initialPageParam: 1,
        queryKey: ['planners', 'upcoming', 'list', userId],
        queryFn: ({ pageParam }) =>
            plannerService.getPaginatedUpcomingPlanners({
                userId,
                pageParam,
                pageSize: PAGE_SIZE,
            }),
        getNextPageParam: (lastPage, allPages) =>
            lastPage.length === PAGE_SIZE ? allPages.length + 1 : undefined,
        enabled: !!userId,
    });
}

// 쿼리 키 상수 정의
export const PLANNER_QUERY_KEYS = {
    all: ['planners'] as const,
    lists: () => [...PLANNER_QUERY_KEYS.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
        [...PLANNER_QUERY_KEYS.lists(), filters] as const,
    details: () => [...PLANNER_QUERY_KEYS.all, 'detail'] as const,
    detail: (id: string) => [...PLANNER_QUERY_KEYS.details(), id] as const,
    byDate: (date: Date) =>
        [...PLANNER_QUERY_KEYS.all, 'byDate', date.toDateString()] as const,
};

// 플래너 생성 뮤테이션 훅
export function useCreatePlanner(onSettled?: () => void) {
    const queryClient = useQueryClient();

    return useMutation<Planner, Error, CreatePlannerInput>({
        mutationFn: newPlanner => plannerService.createPlanner(newPlanner),
        onSuccess: createdPlanner => {
            void invalidatePlannerQueries(queryClient, createdPlanner);
            void queryClient.invalidateQueries({
                queryKey: ['todo', createdPlanner?.todo_id],
            });
        },
        onSettled: onSettled, // 데이터 갱신 완료 후 실행
    });
}

/**
 * Todo와 Planner를 단일 트랜잭션으로 생성하는 뮤테이션 훅
 * 기존의 3단계 과정(Todo 생성 -> Planner 생성 -> Todo 업데이트)을 하나의 API 호출로 처리
 */
export function useCreateTodoWithPlanner(onSettled?: () => void) {
    const queryClient = useQueryClient();

    return useMutation<Planner, Error, CreateTodoWithPlannerInput>({
        mutationFn: input => plannerService.createTodoWithPlanner(input),
        onSuccess: async createdPlanner => {
            // 관련 쿼리 무효화
            await invalidatePlannerQueries(queryClient, createdPlanner);
        },
        onSettled: () => {
            if (onSettled) {
                onSettled();
            }
        },
    });
}

// 플래너 업데이트 뮤테이션 훅
export function useUpdatePlanner() {
    const queryClient = useQueryClient();

    // 알림 발송 함수
    const { createTodoTimeChangedNotification } =
        useTodoTimeChangedNotification();

    return useMutation<
        Planner,
        Error,
        Partial<Planner>,
        UpdateContext<Planner>
    >({
        mutationFn: updatedPlanner =>
            plannerService.updatePlanner(updatedPlanner),

        onMutate: async variables => {
            // 이전 데이터 가져오기
            const oldPlannerData = await plannerService.getPlannerById(
                variables.id
            );

            // 변경 유형 확인
            const isTimeBlockChange = isDifferent(
                variables.blocks,
                oldPlannerData.blocks
            );

            // 변경된 데이터가 없으면 리턴
            if (!isTimeBlockChange) {
                return;
            }

            // 변경된 데이터 반환
            return {
                oldData: oldPlannerData,
                changeTypes: {
                    isTimeBlockChange,
                },
            };
        },

        onSuccess: async (updatedPlanner, variables, context) => {
            await invalidatePlannerQueries(queryClient, updatedPlanner);
            await queryClient.invalidateQueries({
                queryKey: ['todo', updatedPlanner?.todo_id],
            });

            // 변경 관련 알림 발송
            if (!context || !context.oldData) {
                console.log(
                    'Failed to send planner block update notification: context or oldData is undefined'
                );
                return;
            }

            try {
                const { user } = useAuthStore.getState();
                const targetUserId = updatedPlanner.user_id;

                if (user && user.id != targetUserId) {
                    const todo = await todoService.getTodoById(
                        updatedPlanner.todo_id
                    );
                    const todoTitle = todo?.title ?? '';

                    const isMember =
                        await workspaceService.checkWorkspaceMembershipByGoalId(
                            todo.goal_id,
                            targetUserId
                        );
                    if (!isMember) return;

                    await createTodoTimeChangedNotification({
                        userId: targetUserId,
                        todoId: updatedPlanner.todo_id,
                        todoTimeChangedContent: {
                            todo_title: todoTitle,
                            planner_id: updatedPlanner.id,
                            old_time_block: context.oldData.blocks,
                            new_time_block: updatedPlanner.blocks,
                            changer_name: user.name,
                            changer_image_url: user.profile_image_url,
                            changer_id: user.id,
                        },
                    });
                }
            } catch (error) {
                console.error('알림 발송 실패: ', error);
                // 주요 업데이트는 이미 성공했으므로 에러를 무시
            }
        },
    });
}

// 플래너 삭제 뮤테이션 훅
export function useDeletePlanner() {
    const queryClient = useQueryClient();

    return useMutation<
        void,
        Error,
        {
            plannerId: string;
        }
    >({
        mutationFn: ({ plannerId }) => plannerService.deletePlanner(plannerId),
        onSuccess: async (_, variables) => {
            await invalidatePlannerQueries(queryClient, {
                id: variables.plannerId,
            });
            await queryClient.invalidateQueries({
                queryKey: ['todos', 'inbox'],
            });
        },
    });
}

// supabase 실시간 구독
// function useSubscribePlanners() {
//     const queryClient = useQueryClient();

//     useEffect(() => {
//         const channel = supabase
//             .channel('planners')
//             .on(
//                 'postgres_changes',
//                 {
//                     event: '*', // INSERT, UPDATE, DELETE
//                     schema: 'public',
//                     table: 'todo',
//                 },
//                 payload => {
//                     // console.log('Realtime change received:', payload);
//                     // 변경 사항만 업데이트

//                     if (payload.eventType === 'INSERT') {
//                         // 기존 캐시에 push
//                         const insertedPlanner = payload.new as Planner;
//                         queryClient.setQueryData<Planner[]>(
//                             ['planners'],
//                             old =>
//                                 old
//                                     ? [insertedPlanner, ...old]
//                                     : [insertedPlanner]
//                         );
//                     } else if (payload.eventType === 'UPDATE') {
//                         // 기존 캐시에서 수정된 row 찾아서 교체
//                         const updatedPlanner = payload.new as Planner;
//                         queryClient.setQueryData<Planner[]>(['planners'], old =>
//                             old?.map(planner =>
//                                 planner.id === updatedPlanner.id
//                                     ? updatedPlanner
//                                     : planner
//                             )
//                         );
//                     } else if (payload.eventType === 'DELETE') {
//                         // 기존 캐시에서 삭제된 row 찾아서 제거
//                         const deletedPlanner = payload.old as Planner;
//                         queryClient.setQueryData<Planner[]>(['planners'], old =>
//                             old?.filter(
//                                 planner => planner.id !== deletedPlanner.id
//                             )
//                         );
//                     }
//                 }
//             )
//             .subscribe();

//         // 컴포넌트 unmount 시 구독 해제
//         return () => {
//             supabase
//                 .removeChannel(channel) // 비동기 함수여서 아래와 같이 처리
//                 .then(() => {
//                     console.log('Channel removed');
//                 })
//                 .catch(error => {
//                     console.log('Failed to remove channel', error);
//                 });
//         };
//     }, [queryClient]);
// }
